import { BeginnEnde } from './BeginnEnde';
import { useArbeitszeitUpdate } from '../../../../hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pausen } from './Pausen/Pausen';
import { useManagerCanEditArbeitszeiten } from '../../../../hooks/useManagerCanEditArbeitszeit';
import { Bemerkung } from './Bemerkung/Bemerkung';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteArbeitszeit } from './DeleteArbeitszeit';
import { useEditSheetContext } from '../../EditSheetContext';
import { toast } from '@/hooks/use-toast';
import { TermineSelect } from '@/domain/arbeitszeiten/components/TermineSelect';
import { useStandortContext } from '@/features/standortContext/StandortContext';

// eslint-disable-next-line max-lines-per-function
export function Arbeitszeit() {
  const { arbeitszeit } = useEditSheetContext();
  const { currentStandort } = useStandortContext();
  const [arbeitszeitUpdate] = useArbeitszeitUpdate();

  const [isUpdating, setIsUpdating] = useState(false);
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();
  const navigate = useNavigate();
  const { year, month, day } = useParams();

  const { t } = useTranslation();

  // oxlint-disable-next-line complexity
  async function onChangeArbeitszeit(beginn: string, ende: string) {
    if (beginn.length === 0) {
      toast({
        title: t('domain.arbeitszeiten.sheet.arbeitszeit.edit.fehler'),
      });
      return;
    }
    if (
      beginn ===
        `${arbeitszeit.start
          ?.getHours()
          .toString()
          .padStart(2, '0')}:${arbeitszeit.start
          ?.getMinutes()
          .toString()
          .padStart(2, '0')}` &&
      ende ===
        `${arbeitszeit.ende
          ?.getHours()
          .toString()
          .padStart(2, '0')}:${arbeitszeit.ende
          ?.getMinutes()
          .toString()
          .padStart(2, '0')}`
    ) {
      return;
    }
    setIsUpdating(true);
    arbeitszeitUpdate({
      arbeitszeitId: arbeitszeit.id,
      arbeitszeitUpdateInput: {
        beginn: beginn,
        ende: ende === '' ? null : ende,
      },
    })
      .then((result) => {
        if (result.data?.updateArbeitszeit.__typename !== 'Arbeitszeiten') {
          setIsUpdating(false);
          return;
        }
        const resultDate = new Date(result.data.updateArbeitszeit.datum);
        if (
          arbeitszeit.datum?.getDate() !== resultDate.getDate() ||
          arbeitszeit.datum?.getMonth() !== resultDate.getMonth() ||
          arbeitszeit.datum?.getFullYear() !== resultDate.getFullYear()
        ) {
          const newYear = resultDate.getFullYear();
          const newMonth = (resultDate.getMonth() + 1)
            .toString()
            .padStart(2, '0');
          const newDay = resultDate.getDate().toString().padStart(2, '0');

          toast({
            title: t('domain.arbeitszeiten.create.toast.tageswechsel.title'),
            description: t(
              'domain.arbeitszeiten.create.toast.tageswechsel.description',
              {
                date: `${newDay}.${newMonth}.${newYear}`,
              },
            ),
          });
          setIsUpdating(false);
          return;
        }

        toast({
          title: t('domain.arbeitszeiten.sheet.arbeitszeit.edit.erfolgreich'),
        });
        setIsUpdating(false);
      })
      .catch(() => {
        toast({
          title: t('domain.arbeitszeiten.sheet.arbeitszeit.edit.fehler'),
        });
        setIsUpdating(false);
      });
  }

  function handleDelete() {
    navigate(`/arbeitszeiten/uebersicht/${year}/${month}/${day}`);
  }

  return (
    <div className="flex flex-col gap-4 min-h-fit">
      <BeginnEnde
        beginn={arbeitszeit.beginn2}
        ende={arbeitszeit.ende2}
        kommen={arbeitszeit.kommen2}
        gehen={arbeitszeit.gehen2}
        onChange={(e) => onChangeArbeitszeit(e.beginn ?? '', e.ende ?? '')}
        disabled={isUpdating || !canEditArbeitszeit}
      />

      {arbeitszeit.abwesenheit === undefined && (
        <Pausen arbeitszeit={arbeitszeit} />
      )}

      <Bemerkung arbeitszeit={arbeitszeit} />

      {currentStandort.isTerminVerknuepfung && (
        <TermineSelect
          arbeitszeit={arbeitszeit}
          datum={`${year}-${month}-${day}`}
        />
      )}

      {canEditArbeitszeit && (
        <div className="mt-auto text-end">
          <DeleteArbeitszeit
            arbeitszeit={arbeitszeit}
            onDeleted={handleDelete}
            isDeleting={false}
          />
        </div>
      )}
    </div>
  );
}
